'use client';

import React, { Component, ReactNode, ErrorInfo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetKeys?: Array<string | number>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary - Catches errors in child components and displays fallback UI.
 * Usage: <ErrorBoundary><YourComponent /></ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error);
    console.error('Error Info:', errorInfo);
    this.setState({ error, errorInfo });
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    const { resetKeys } = this.props;
    const { hasError } = this.state;
    if (hasError && resetKeys && prevProps.resetKeys) {
      const hasChanged = resetKeys.some((key, i) => key !== prevProps.resetKeys?.[i]);
      if (hasChanged) this.reset();
    }
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      if (fallback) return fallback;

      return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4 py-8">
          <div className="max-w-2xl w-full bg-gray-800 rounded-xl border border-gray-700 p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Something went wrong</h1>
                <p className="text-gray-400 mt-1">We encountered an unexpected error</p>
              </div>
            </div>
            {process.env.NODE_ENV === 'development' && error && (
              <div className="mb-6 bg-gray-900 rounded-lg p-4 border border-gray-700">
                <h2 className="text-sm font-semibold text-red-400 mb-2">Error Details:</h2>
                <p className="text-sm text-gray-300 font-mono mb-2">{error.toString()}</p>
                {errorInfo?.componentStack && (
                  <details className="mt-3">
                    <summary className="text-sm text-gray-400 cursor-pointer hover:text-gray-300">
                      Component Stack
                    </summary>
                    <pre className="mt-2 text-xs text-gray-500 overflow-x-auto">
                      {errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={this.reset} variant="primary" className="flex-1">
                Try Again
              </Button>
              <Button onClick={() => window.location.href = '/'} variant="secondary" className="flex-1">
                Go Home
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return children;
  }
}
